import {
  collection,
  query,
  where,
  getDocs,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getMaintenanceByDeliveryDateRange } from './maintenanceService';
import { getAllExpenses } from './expenseService';

// أنواع البيانات للتقرير الشهري
export interface DailyDrawerSummary {
  date: string;
  salesIncome: number;
  maintenanceIncome: number;
  dailyExpenses: number;
  netDailyProfit: number;
}

export interface MonthlyExpensesSummary {
  totalBasicExpenses: number;
  totalDailyExpenses?: number; // إضافة المصاريف اليومية
  expensesByCategory: Record<string, number>;
  expenseDetails: Array<{
    category: string;
    amount: number;
    description: string;
    date: Date;
  }>;
}

export interface SalesPartsProfitSummary {
  totalSalesRevenue: number;
  totalPartsCost: number;
  totalPartsProfit: number;
  profitMargin: number;
  salesByCategory: Record<string, {
    revenue: number;
    cost: number;
    profit: number;
    quantity: number;
  }>;
}

export interface MaintenanceProfitSummary {
  totalMaintenanceRevenue: number; // إجمالي إيرادات الصيانة
  totalMaintenancePartsCost: number; // تكلفة قطع الغيار في الصيانة (بسعر الشراء)
  totalTechnicianFees: number; // إجمالي نسب الفنيين
  totalMaintenanceServiceFees: number; // أجر الصيانة (بدون قطع الغيار)
  netMaintenanceProfit: number; // صافي ربح الصيانة
}

export interface MonthlyReport {
  month: string;
  year: number;
  
  // تقرير الدرج اليومي
  dailyDrawerSummary: DailyDrawerSummary[];
  totalDrawerProfit: number;
  
  // ربح الصيانة المفصل
  maintenanceProfit: MaintenanceProfitSummary;
  
  // المصاريف الشهرية
  monthlyExpenses: MonthlyExpensesSummary;
  
  // مكسب المبيعات (فرق سعر قطع الغيار)
  salesPartsProfit: SalesPartsProfitSummary;
  
  // التقرير النهائي للمكسب والخسارة
  finalProfitLoss: {
    totalMaintenanceProfit: number; // صافي ربح الصيانة
    totalBasicExpenses: number; // المصاريف الأساسية الشهرية
    totalDailyExpenses: number; // إجمالي المصاريف اليومية
    totalSalesPartsProfit: number; // ربح قطع الغيار من المبيعات
    netMonthlyProfit: number; // صافي الربح الشهري
    profitMargin: number;
  };
}

/**
 * حساب ربح الصيانة المفصل لشهر محدد
 */
export const getMaintenanceProfitForMonth = async (year: number, month: number): Promise<MaintenanceProfitSummary> => {
  try {
    console.log(`🔧 حساب ربح الصيانة المفصل لشهر ${month}/${year} (بنفس منطق صفحة التقارير)`);

    // تحديد بداية ونهاية الشهر
    const startOfMonth = new Date(year, month - 1, 1);
    const endOfMonth = new Date(year, month, 0, 23, 59, 59, 999);

    console.log(`📅 البحث عن طلبات الصيانة المُسلمة من ${startOfMonth.toLocaleDateString()} إلى ${endOfMonth.toLocaleDateString()}`);

    // استخدام نفس منطق صفحة التقارير - جلب الطلبات المُسلمة حسب تاريخ التسليم
    const deliveredMaintenanceRecords = await getMaintenanceByDeliveryDateRange(startOfMonth, endOfMonth);
    console.log(`📋 تم جلب ${deliveredMaintenanceRecords.length} طلب صيانة مُسلم`);

    if (deliveredMaintenanceRecords.length === 0) {
      console.log('⚠️ لا توجد طلبات صيانة مُسلمة في هذا الشهر');
      return {
        totalMaintenanceRevenue: 0,
        totalMaintenancePartsCost: 0,
        totalTechnicianFees: 0,
        totalMaintenanceServiceFees: 0,
        netMaintenanceProfit: 0
      };
    }

    // تحليل بيانات الطلبات المُسلمة (نفس منطق صفحة التقارير)
    let totalMaintenanceRevenue = 0;
    let totalMaintenancePartsCost = 0;
    let totalTechnicianFees = 0;

    deliveredMaintenanceRecords.forEach(record => {
      console.log(`🔧 تحليل طلب: ${record.id} - الحالة: ${record.status} - المبلغ: ${record.finalCost || record.paid || 0}`);
      
      // إجمالي إيرادات الصيانة (نفس حساب صفحة التقارير)
      const revenue = record.finalCost || record.paid || 0;
      totalMaintenanceRevenue += revenue;
      
      // تكلفة قطع الغيار (بسعر الشراء)
      if (record.usedParts && record.usedParts.length > 0) {
        record.usedParts.forEach((part: any) => {
          const quantity = Number(part.quantity) || 0;
          const purchasePrice = Number(part.purchasePrice) || 0;
          const partCost = purchasePrice * quantity;
          totalMaintenancePartsCost += partCost;
          console.log(`📦 قطعة غيار: ${part.name} - كمية: ${quantity} - سعر شراء: ${purchasePrice} - إجمالي: ${partCost}`);
        });
      }
      
      // نسبة الفني (مؤقتاً لا نخصمها لتطابق صفحة التقارير العادية)
      if (record.technicianShare && record.technicianShare > 0) {
        // totalTechnicianFees += record.technicianShare; // معطل مؤقتاً
        console.log(`👨‍🔧 طلب ${record.id}: نسبة فني موجودة (${record.technicianShare}) لكن لا نخصمها مؤقتاً`);
      } else {
        console.log(`👨‍🔧 طلب ${record.id}: لا توجد نسبة فني`);
      }
    });

    // حساب أجر الصيانة (الإيرادات الكلية)  
    const totalMaintenanceServiceFees = totalMaintenanceRevenue;
    
    // حساب صافي ربح الصيانة (مؤقتاً مثل صفحة التقارير العادية - بدون خصم نسب الفنيين)
    // صافي ربح الصيانة = إجمالي الإيرادات - تكلفة قطع الغيار فقط
    const netMaintenanceProfit = totalMaintenanceRevenue - totalMaintenancePartsCost;

    console.log(`🔧 نتائج ربح الصيانة (مؤقتاً مطابقة لصفحة التقارير العادية):`, {
      totalMaintenanceRevenue: `${totalMaintenanceRevenue} جنيه (إجمالي الإيرادات)`,
      totalMaintenancePartsCost: `${totalMaintenancePartsCost} جنيه (تكلفة قطع الغيار)`,
      totalTechnicianFees: `${totalTechnicianFees} جنيه (نسب الفنيين - لا تُخصم مؤقتاً)`,
      totalMaintenanceServiceFees: `${totalMaintenanceServiceFees} جنيه (أجر الصيانة)`,
      netMaintenanceProfit: `${netMaintenanceProfit} جنيه (صافي الربح)`,
      calculation: `${totalMaintenanceRevenue} - ${totalMaintenancePartsCost} = ${netMaintenanceProfit} (بدون خصم نسب الفنيين مؤقتاً)`
    });

    return {
      totalMaintenanceRevenue,
      totalMaintenancePartsCost,
      totalTechnicianFees,
      totalMaintenanceServiceFees,
      netMaintenanceProfit
    };
  } catch (error) {
    console.error('Error calculating maintenance profit:', error);
    return {
      totalMaintenanceRevenue: 0,
      totalMaintenancePartsCost: 0,
      totalTechnicianFees: 0,
      totalMaintenanceServiceFees: 0,
      netMaintenanceProfit: 0
    };
  }
};

/**
 * الحصول على المصاريف الأساسية (الشهرية) لشهر محدد
 */
export const getExpensesForMonth = async (year: number, month: number): Promise<MonthlyExpensesSummary> => {
  try {
    console.log(`📊 جلب المصاريف الشهرية لشهر ${month}/${year} (بنفس منطق صفحة المصروفات)`);

    // تحديد بداية ونهاية الشهر
    const startOfMonth = new Date(year, month - 1, 1);
    const endOfMonth = new Date(year, month, 0, 23, 59, 59, 999);

    // استخدام نفس منطق صفحة المصروفات - جلب جميع المصروفات ثم تصفيتها
    const allExpenses = await getAllExpenses();
    console.log(`📋 تم جلب ${allExpenses.length} مصروف إجمالي`);

    // تصفية المصروفات الخاصة بالشهر المطلوب
    const monthExpenses = allExpenses.filter(expense => {
      const expenseDate = expense.date;
      return expenseDate >= startOfMonth && expenseDate <= endOfMonth;
    });

    console.log(`📋 تم تصفية ${monthExpenses.length} مصروف للشهر المطلوب`);

    // حساب إجمالي المصروفات حسب النوع (نفس منطق صفحة المصروفات)
    let totalBasicExpenses = 0;
    let totalDailyExpenses = 0;
    const expensesByCategory: Record<string, number> = {};
    const expenseDetails: Array<{
      category: string;
      amount: number;
      description: string;
      date: Date;
    }> = [];

    // تصنيف المصروفات حسب النوع (نفس منطق صفحة المصروفات)
    monthExpenses.forEach(expense => {
      if (expense.type === 'basic') {
        totalBasicExpenses += expense.amount;
      } else if (expense.type === 'daily') {
        totalDailyExpenses += expense.amount;
      }

      // تجميع حسب الفئة
      const category = expense.category || 'غير محدد';
      expensesByCategory[category] = (expensesByCategory[category] || 0) + expense.amount;
      
      expenseDetails.push({
        category: expense.category || 'غير محدد',
        amount: expense.amount,
        description: expense.description || '',
        date: expense.date
      });
    });

    console.log(`💰 إجمالي المصاريف الأساسية: ${totalBasicExpenses} جنيه`);
    console.log(`💰 إجمالي المصاريف اليومية: ${totalDailyExpenses} جنيه`);
    console.log(`💰 إجمالي المصاريف الكلي: ${totalBasicExpenses + totalDailyExpenses} جنيه`);
    console.log(`📋 المصاريف حسب التصنيف:`, expensesByCategory);

    return {
      totalBasicExpenses,
      totalDailyExpenses, // إضافة المصاريف اليومية أيضاً
      expensesByCategory,
      expenseDetails
    };
  } catch (error) {
    console.error('Error getting expenses for month:', error);
    throw new Error('فشل في جلب المصاريف للشهر');
  }
};

/**
 * حساب مكسب المبيعات من فرق أسعار قطع الغيار
 */
export const getSalesPartsProfitForMonth = async (year: number, month: number): Promise<SalesPartsProfitSummary> => {
  try {
    console.log(`🛒 حساب مكسب المبيعات لشهر ${month}/${year}`);
    
    // تحديد بداية ونهاية الشهر
    const startOfMonth = new Date(year, month - 1, 1);
    const endOfMonth = new Date(year, month, 0, 23, 59, 59, 999);
    
    // جلب المبيعات
    const salesQuery = query(
      collection(db, 'sales'),
      where('createdAt', '>=', startOfMonth),
      where('createdAt', '<=', endOfMonth),
      orderBy('createdAt', 'desc')
    );
    
    const salesSnapshot = await getDocs(salesQuery);

    console.log(`🛒 تم جلب ${salesSnapshot.docs.length} عملية بيع`);

    const sales = salesSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        total: data.total || 0,
        items: data.items || [],
        customerName: data.customerName || '',
        paymentMethod: data.paymentMethod || 'cash',
        createdAt: data.createdAt ? data.createdAt.toDate() : new Date(),
        updatedAt: data.updatedAt,
        createdBy: data.createdBy,
        branchId: data.branchId
      };
    });
    
    let totalSalesRevenue = 0;
    let totalPartsCost = 0;
    let totalPartsProfit = 0;
    const salesByCategory: Record<string, {
      revenue: number;
      cost: number;
      profit: number;
      quantity: number;
    }> = {};
    
    sales.forEach(sale => {
      totalSalesRevenue += sale.total || 0;
      
      // حساب تكلفة وربح قطع الغيار
      if (sale.items && Array.isArray(sale.items)) {
        sale.items.forEach((item: any) => {
          const itemRevenue = (item.price || 0) * (item.quantity || 0);
          const itemCost = (item.purchasePrice || item.price * 0.7) * (item.quantity || 0); // افتراض 70% تكلفة إذا لم تكن محددة
          const itemProfit = itemRevenue - itemCost;
          
          totalPartsCost += itemCost;
          totalPartsProfit += itemProfit;
          
          const category = item.category || 'غير محدد';
          if (!salesByCategory[category]) {
            salesByCategory[category] = { revenue: 0, cost: 0, profit: 0, quantity: 0 };
          }
          
          salesByCategory[category].revenue += itemRevenue;
          salesByCategory[category].cost += itemCost;
          salesByCategory[category].profit += itemProfit;
          salesByCategory[category].quantity += item.quantity || 0;
        });
      }
    });
    
    const profitMargin = totalSalesRevenue > 0 ? (totalPartsProfit / totalSalesRevenue) * 100 : 0;
    
    console.log(`🛒 إجمالي إيرادات المبيعات: ${totalSalesRevenue} جنيه`);
    console.log(`💸 إجمالي تكلفة قطع الغيار: ${totalPartsCost} جنيه`);
    console.log(`💰 إجمالي ربح المبيعات: ${totalPartsProfit} جنيه`);
    console.log(`📊 هامش الربح: ${profitMargin.toFixed(2)}%`);
    
    return {
      totalSalesRevenue,
      totalPartsCost,
      totalPartsProfit,
      profitMargin,
      salesByCategory
    };
  } catch (error) {
    console.error('Error calculating sales parts profit:', error);
    throw new Error('فشل في حساب مكسب المبيعات');
  }
};

/**
 * دالة اختبار مبسطة للتقرير الشهري
 */
export const generateSimpleMonthlyReport = async (year: number, month: number): Promise<MonthlyReport> => {
  try {
    console.log(`🧪 إنشاء تقرير شهري مبسط لشهر ${month}/${year}`);

    // إنشاء تقرير أساسي بدون بيانات معقدة
    const report: MonthlyReport = {
      month: getMonthName(month),
      year,
      dailyDrawerSummary: [],
      totalDrawerProfit: 0,
      maintenanceProfit: {
        totalMaintenanceRevenue: 0,
        totalMaintenancePartsCost: 0,
        totalTechnicianFees: 0,
        totalMaintenanceServiceFees: 0,
        netMaintenanceProfit: 0
      },
      monthlyExpenses: {
        totalBasicExpenses: 0,
        expensesByCategory: {},
        expenseDetails: []
      },
      salesPartsProfit: {
        totalSalesRevenue: 0,
        totalPartsCost: 0,
        totalPartsProfit: 0,
        profitMargin: 0,
        salesByCategory: {}
      },
      finalProfitLoss: {
        totalMaintenanceProfit: 0,
        totalBasicExpenses: 0,
        totalDailyExpenses: 0,
        totalSalesPartsProfit: 0,
        netMonthlyProfit: 0,
        profitMargin: 0
      }
    };

    console.log('✅ تم إنشاء التقرير المبسط بنجاح');
    return report;
  } catch (error) {
    console.error('❌ خطأ في إنشاء التقرير المبسط:', error);
    throw error;
  }
};

/**
 * إنشاء التقرير الشهري الشامل
 */
export const generateMonthlyReport = async (year: number, month: number): Promise<MonthlyReport> => {
  try {
    // التحقق من صحة المعاملات
    if (!year || year < 2020 || year > 2030) {
      throw new Error(`سنة غير صحيحة: ${year}`);
    }
    if (!month || month < 1 || month > 12) {
      throw new Error(`شهر غير صحيح: ${month}`);
    }

    console.log(`📊 إنشاء التقرير الشهري الشامل لشهر ${month}/${year}`);

    // جلب البيانات بشكل تسلسلي للتشخيص
    console.log('🔄 بدء جلب تقرير الدرج اليومي...');
    // const dailyDrawerSummary = await getDailyDrawerSummaryForMonth(year, month);
    // TODO: تحديث منطق الدرج الشهري ليعتمد على الخزينة الجديدة
    // console.log('✅ تم جلب تقرير الدرج اليومي:', dailyDrawerSummary.length, 'يوم');

    console.log('🔄 بدء جلب المصاريف الشهرية...');
    let monthlyExpenses;
    try {
      monthlyExpenses = await getExpensesForMonth(year, month);
      console.log('✅ تم جلب المصاريف:', 
        `أساسية: ${monthlyExpenses.totalBasicExpenses} جنيه,`,
        `يومية: ${monthlyExpenses.totalDailyExpenses || 0} جنيه`
      );
    } catch (expensesError) {
      console.error('❌ خطأ في جلب المصاريف:', expensesError);
      monthlyExpenses = {
        totalBasicExpenses: 0,
        totalDailyExpenses: 0,
        expensesByCategory: {},
        expenseDetails: []
      };
    }

    console.log('🔄 بدء جلب ربح الصيانة المفصل...');
    let maintenanceProfit;
    try {
      maintenanceProfit = await getMaintenanceProfitForMonth(year, month);
      console.log('✅ تم جلب ربح الصيانة المفصل:', maintenanceProfit.netMaintenanceProfit, 'جنيه');
    } catch (maintenanceError) {
      console.error('❌ خطأ في جلب ربح الصيانة:', maintenanceError);
      maintenanceProfit = {
        totalMaintenanceRevenue: 0,
        totalMaintenancePartsCost: 0,
        totalTechnicianFees: 0,
        totalMaintenanceServiceFees: 0,
        netMaintenanceProfit: 0
      };
    }

    console.log('🔄 بدء جلب ربح المبيعات...');
    let salesPartsProfit;
    try {
      salesPartsProfit = await getSalesPartsProfitForMonth(year, month);
      console.log('✅ تم جلب ربح المبيعات:', salesPartsProfit.totalPartsProfit, 'جنيه');
    } catch (salesError) {
      console.error('❌ خطأ في جلب ربح المبيعات:', salesError);
      salesPartsProfit = {
        totalSalesRevenue: 0,
        totalPartsCost: 0,
        totalPartsProfit: 0,
        profitMargin: 0,
        salesByCategory: {}
      };
    }

    // استخدام المصاريف المباشرة من جدول المصروفات (نفس منطق صفحة المصروفات)
    const totalDailyExpenses = monthlyExpenses.totalDailyExpenses || 0;
    
    console.log(`💰 تفاصيل المصاريف الشهرية:`, {
      totalBasicExpenses: monthlyExpenses.totalBasicExpenses,
      totalDailyExpenses: totalDailyExpenses,
      totalCombined: monthlyExpenses.totalBasicExpenses + totalDailyExpenses
    });

    // حساب التقرير النهائي للمكسب والخسارة حسب المعادلة الجديدة
    // صافي الربح الشهري = (صافي ربح الصيانة) - (المصاريف الأساسية + المصاريف اليومية) + (ربح المبيعات)
    const finalProfitLoss = {
      totalMaintenanceProfit: maintenanceProfit.netMaintenanceProfit,
      totalBasicExpenses: monthlyExpenses.totalBasicExpenses,
      totalDailyExpenses: totalDailyExpenses,
      totalSalesPartsProfit: salesPartsProfit.totalPartsProfit,
      netMonthlyProfit: maintenanceProfit.netMaintenanceProfit - monthlyExpenses.totalBasicExpenses - totalDailyExpenses + salesPartsProfit.totalPartsProfit,
      profitMargin: 0
    };

    // حساب هامش الربح الإجمالي
    // const totalRevenue = dailyDrawerSummary.reduce((sum: number, day: any) => sum + day.salesIncome + day.maintenanceIncome, 0);
    // finalProfitLoss.profitMargin = totalRevenue > 0 ? (finalProfitLoss.netMonthlyProfit / totalRevenue) * 100 : 0;

    // حساب إجمالي ربح الدرج للمحافظة على التوافق مع الواجهة القديمة
    // const totalDrawerProfit = dailyDrawerSummary.reduce((sum: number, day: any) => sum + day.netDailyProfit, 0);

    return {
      month: getMonthName(month),
      year,
      dailyDrawerSummary: [], // TODO: تحديث لاحقاً بمنطق الخزينة الجديد
      totalDrawerProfit: 0,  // TODO: تحديث لاحقاً بمنطق الخزينة الجديد
      maintenanceProfit,
      monthlyExpenses,
      salesPartsProfit,
      finalProfitLoss
    };
  } catch (error) {
    console.error('Error generating monthly report:', error);
    throw new Error('فشل في إنشاء التقرير الشهري');
  }
};

/**
 * الحصول على اسم الشهر بالعربية
 */
const getMonthName = (month: number): string => {
  const monthNames = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];
  return monthNames[month - 1] || 'غير محدد';
};

/**
 * تصدير التقرير الشهري إلى PDF
 */
export const exportMonthlyReportToPDF = async (report: MonthlyReport): Promise<void> => {
  try {
    console.log('📄 تصدير التقرير الشهري إلى PDF...');
    console.log('Report data:', report.month, report.year);

    // سيتم تطوير هذه الدالة لاحقاً
    console.log('PDF export will be implemented later');

  } catch (error) {
    console.error('Error exporting monthly report to PDF:', error);
    throw new Error('فشل في تصدير التقرير إلى PDF');
  }
};

/**
 * تصدير التقرير الشهري إلى Excel
 */
export const exportMonthlyReportToExcel = async (report: MonthlyReport): Promise<void> => {
  try {
    console.log('📊 تصدير التقرير الشهري إلى Excel...');
    console.log('Report data:', report.month, report.year);

    // سيتم تطوير هذه الدالة لاحقاً
    console.log('Excel export will be implemented later');

  } catch (error) {
    console.error('Error exporting monthly report to Excel:', error);
    throw new Error('فشل في تصدير التقرير إلى Excel');
  }
};
